// src/pages/PartnerEmpresaClientes.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Card,
  CardContent,
  Divider,
  Stack,
  Avatar,
  Chip,
  Grid,
  alpha,
  useTheme,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";

import { useAuth } from "../contexts/AuthContext";
import { db } from "../contexts/firebase/config";
import { collection, query, where, onSnapshot } from "firebase/firestore";

import sintanetLogo from "../assets/sintanet-logo.png";

export default function PartnerEmpresaClientes() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const colors = {
    accent: "#ff6a00",
    success: "#ff8a1f",
    card: isDark ? "rgba(15,23,42,0.9)" : "rgba(255,255,255,0.94)",
    border: isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)",
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
    hero: "#080808",
  };

  const { currentUser } = useAuth();
  const empresaId = currentUser?.uid;

  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!empresaId) return;

    const q = query(
      collection(db, "clientes"),
      where("empresaId", "==", empresaId)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setClientes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      () => setLoading(false)
    );

    return unsub;
  }, [empresaId]);

  const renovados = useMemo(
    () => clientes.filter((c) => c.estado === "Renovação").length,
    [clientes]
  );

  const novos = useMemo(
    () => clientes.filter((c) => c.estado !== "Renovação").length,
    [clientes]
  );

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 76px)",
        width: "100%",
        position: "relative",
        px: { xs: 1.5, sm: 2.5, md: 3.5 },
        py: { xs: 2, md: 3 },
        background: isDark
          ? "linear-gradient(135deg, #020617 0%, #0f172a 55%, #111827 100%)"
          : "linear-gradient(135deg, #f8fafc 0%, #fff7ed 48%, #f8fafc 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: isDark ? 0.16 : 0.28,
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.16) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "linear-gradient(to bottom, black, transparent 72%)",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: "1520px",
          mx: "auto",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: { xs: 2.4, md: 3.6 },
            borderRadius: "32px",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.14)",
            overflow: "hidden",
            position: "relative",
            boxShadow: isDark
              ? "0 26px 80px rgba(0,0,0,0.34)"
              : "0 24px 70px rgba(15,23,42,0.2)",
            background:
              "radial-gradient(circle at 82% 28%, rgba(255,106,0,0.42) 0%, rgba(255,106,0,0.18) 24%, transparent 48%), linear-gradient(135deg, #05070b 0%, #121212 52%, #ff6a00 180%)",
            "&:before": {
              content: '""',
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${sintanetLogo})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: {
                xs: "center 22px",
                lg: "right 60px center",
              },
              backgroundSize: {
                xs: "260px auto",
                md: "420px auto",
                lg: "520px auto",
              },
              opacity: 0.12,
              filter: "drop-shadow(0 24px 50px rgba(255,106,0,0.35))",
              pointerEvents: "none",
            },
          }}
        >
          <Grid container spacing={3} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
            <Grid item xs={12} lg={8}>
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                <Avatar
                  src={sintanetLogo}
                  variant="rounded"
                  sx={{
                    width: 54,
                    height: 54,
                    borderRadius: "18px",
                    bgcolor: "rgba(255,106,0,0.16)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    p: 0.8,
                    "& img": { objectFit: "contain" },
                  }}
                />

                <Typography
                  variant="overline"
                  sx={{
                    fontSize: 12,
                    fontWeight: 900,
                    letterSpacing: "0.18em",
                    color: "rgba(255,255,255,0.72)",
                  }}
                >
                  Clientes da Empresa
                </Typography>
              </Stack>

              <Typography
                sx={{
                  fontSize: { xs: "2.15rem", sm: "3rem", md: "4rem" },
                  fontWeight: 950,
                  lineHeight: 0.95,
                  letterSpacing: "-0.08em",
                  color: "#fff",
                }}
              >
                Gestão de Clientes
              </Typography>

              <Typography
                sx={{
                  mt: 1.7,
                  maxWidth: 820,
                  color: "rgba(255,255,255,0.72)",
                  fontSize: { xs: "0.98rem", md: "1.08rem" },
                  lineHeight: 1.75,
                }}
              >
                Consulta todos os clientes associados à tua empresa num painel moderno
                com a identidade visual da Sintanet.
              </Typography>

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2.4 }}>
                <Chip
                  icon={<WorkspacePremiumRoundedIcon />}
                  label="Área Parceiro"
                  sx={{
                    height: 36,
                    borderRadius: 999,
                    fontWeight: 900,
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.16)",
                    "& .MuiChip-icon": { color: "#ff8a1f" },
                  }}
                />

                <Chip
                  label={`${clientes.length} clientes`}
                  sx={{
                    height: 36,
                    borderRadius: 999,
                    fontWeight: 900,
                    color: "#fff",
                    bgcolor: "rgba(255,106,0,0.26)",
                    border: "1px solid rgba(255,255,255,0.16)",
                  }}
                />
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 3 }}>
                <Button
                  component={RouterLink}
                  to="/dashboard"
                  variant="contained"
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 950,
                    px: 2.4,
                    bgcolor: "#ff6a00",
                    color: "#fff",
                    boxShadow: "0 14px 35px rgba(255,106,0,0.35)",
                    "&:hover": { bgcolor: "#ff7a1a" },
                  }}
                >
                  Voltar à Dashboard
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.2,
                  borderRadius: "26px",
                  bgcolor: "rgba(10,10,10,0.48)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  backdropFilter: "blur(18px)",
                }}
              >
                <Stack spacing={1.4}>
                  {[
                    ["Total clientes", clientes.length, <PeopleAltRoundedIcon />],
                    ["Novos", novos, <PersonRoundedIcon />],
                    ["Renovados", renovados, <CheckCircleRoundedIcon />],
                  ].map(([label, value, icon]) => (
                    <Stack
                      key={label}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Stack direction="row" spacing={1.2} alignItems="center">
                        <Avatar
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: "16px",
                            bgcolor: "rgba(255,106,0,0.22)",
                            color: "#fff",
                          }}
                        >
                          {icon}
                        </Avatar>

                        <Typography sx={{ color: "rgba(255,255,255,0.68)", fontWeight: 800 }}>
                          {label}
                        </Typography>
                      </Stack>

                      <Typography sx={{ color: "#fff", fontWeight: 950, fontSize: "1.25rem" }}>
                        {loading ? "…" : value}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: colors.accent }} />
          </Box>
        ) : clientes.length === 0 ? (
          <Card
            elevation={0}
            sx={{
              maxWidth: 560,
              mx: "auto",
              borderRadius: "30px",
              textAlign: "center",
              py: 4,
              px: 2,
              bgcolor: colors.card,
              border: `1px solid ${colors.border}`,
              boxShadow: isDark
                ? "0 24px 64px rgba(0,0,0,0.28)"
                : "0 24px 60px rgba(15,23,42,0.07)",
            }}
          >
            <CardContent>
              <Avatar
                src={sintanetLogo}
                variant="rounded"
                sx={{
                  width: 72,
                  height: 72,
                  mx: "auto",
                  mb: 2,
                  borderRadius: "24px",
                  bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                  p: 1,
                  "& img": { objectFit: "contain" },
                }}
              />

              <Typography variant="h6" sx={{ fontWeight: 950, color: colors.text }} gutterBottom>
                Nenhum cliente encontrado
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Ainda não existem clientes registados nesta empresa.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Paper
            elevation={0}
            sx={{
              borderRadius: "30px",
              border: `1px solid ${colors.border}`,
              bgcolor: colors.card,
              boxShadow: isDark
                ? "0 24px 64px rgba(0,0,0,0.28)"
                : "0 24px 60px rgba(15,23,42,0.07)",
              overflow: "hidden",
            }}
          >
            <Box sx={{ px: { xs: 2.25, md: 3 }, py: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Stack direction="row" spacing={1.45} alignItems="center" sx={{ minWidth: 0 }}>
                  <Avatar
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: "17px",
                      bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                      color: colors.accent,
                    }}
                  >
                    <PersonRoundedIcon />
                  </Avatar>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" fontWeight={950} sx={{ color: colors.text }}>
                      Lista de Clientes
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                      {clientes.length} cliente(s) associado(s) à empresa.
                    </Typography>
                  </Box>
                </Stack>

                <Chip
                  label={`${novos} novo(s)`}
                  sx={{
                    flexShrink: 0,
                    borderRadius: "999px",
                    fontWeight: 900,
                    bgcolor: alpha(colors.accent, isDark ? 0.16 : 0.08),
                    color: colors.accent,
                  }}
                />
              </Stack>
            </Box>

            <Divider sx={{ borderColor: colors.border }} />

            <TableContainer component={Paper} elevation={0} sx={{ bgcolor: "transparent" }}>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        bgcolor: isDark ? "rgba(2,6,23,0.38)" : "rgba(248,250,252,0.92)",
                        fontWeight: 900,
                        color: colors.muted,
                        borderBottom: `1px solid ${colors.border}`,
                        fontSize: "0.76rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        py: 1.45,
                      },
                    }}
                  >
                    <TableCell>Nome</TableCell>
                    <TableCell>Plano</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {clientes.map((c) => {
                    const renovado = c.estado === "Renovação";

                    return (
                      <TableRow
                        key={c.id}
                        hover
                        sx={{
                          "& td": {
                            borderBottom: `1px solid ${colors.border}`,
                            py: 1.7,
                          },
                          "&:last-child td": { borderBottom: 0 },
                          "&:hover": {
                            bgcolor: alpha(colors.accent, isDark ? 0.08 : 0.045),
                          },
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1.2} alignItems="center">
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                fontSize: 14,
                                fontWeight: 900,
                                bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                                color: colors.accent,
                              }}
                            >
                              {c.nome?.charAt(0)?.toUpperCase() || "C"}
                            </Avatar>

                            <Typography sx={{ fontWeight: 850, color: colors.text }}>
                              {c.nome || "Sem nome"}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={c.plano || "—"}
                            sx={{
                              borderRadius: "999px",
                              fontWeight: 850,
                              bgcolor: alpha(colors.accent, isDark ? 0.12 : 0.06),
                              color: colors.text,
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={renovado ? "Renovado" : "Novo"}
                            sx={{
                              borderRadius: "999px",
                              fontWeight: 900,
                              bgcolor: renovado
                                ? alpha(colors.success, isDark ? 0.18 : 0.1)
                                : alpha(colors.accent, isDark ? 0.16 : 0.08),
                              color: renovado ? colors.success : colors.accent,
                            }}
                          />
                        </TableCell>

                        <TableCell align="right">
                          <Button
                            component={RouterLink}
                            to={`/dashboard/empresa/cliente/${c.id}`}
                            variant="contained"
                            size="small"
                            endIcon={<ArrowForwardRoundedIcon />}
                            sx={{
                              textTransform: "none",
                              borderRadius: "999px",
                              px: 2.2,
                              py: 0.75,
                              fontWeight: 900,
                              boxShadow: "none",
                              bgcolor: colors.accent,
                              color: "#fff",
                              "&:hover": {
                                bgcolor: "#ff7a1a",
                                boxShadow: "0 10px 24px rgba(255,106,0,0.25)",
                              },
                            }}
                          >
                            Ver Detalhes
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
    </Box>
  );
}